import { Download, NavigateBefore, NavigateNext, Refresh } from '@mui/icons-material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  FOCUS_MODE_BACKGROUNDS,
  FOCUS_MODE_BORDERS,
  FOCUS_MODE_BUTTON_BACKGROUNDS,
  FOCUS_MODE_COLORS,
  FOCUS_MODE_GRADIENTS,
  FOCUS_MODE_SHADOWS,
  getFocusModeValue,
} from '@src/constants/design-tokens';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import { memo, type ReactElement, useCallback } from 'react';
import { useSelector } from 'react-redux';

/**
 * 関数エリアコンポーネントのProps型定義
 */
type FunctionAreaProps = {
  page: number;
  pageTotal: number;
  pdfUrl: string;
  isScrolling: boolean;
  isVisible: boolean;
  isRirekishoMode?: boolean;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
  onDownload: () => void;
};

/**
 * メモ化された関数エリアコンポーネント
 *
 * PDFプレビューの操作ボタン（前のページ、次のページ、リフレッシュ、ダウンロード）を提供します。
 *
 * @param {FunctionAreaProps} props - コンポーネントのプロパティ
 * @param {number} props.page - 現在のページ番号
 * @param {number} props.pageTotal - 総ページ数
 * @param {string} props.pdfUrl - PDFのURL
 * @param {(page: number) => void} props.onPageChange - ページ変更時のコールバック
 * @param {() => void} props.onRefresh - リフレッシュ時のコールバック
 * @param {() => void} props.onDownload - ダウンロード時のコールバック
 * @returns {ReactElement} 関数エリアコンポーネント
 *
 * @example
 * ```tsx
 * <FunctionArea
 *   page={1}
 *   pageTotal={5}
 *   pdfUrl="https://example.com/pdf"
 *   onPageChange={(page) => setPage(page)}
 *   onRefresh={() => refreshPdf()}
 *   onDownload={() => downloadPdf()}
 * />
 * ```
 */
export const FunctionArea = memo(
  ({
    page,
    pageTotal,
    pdfUrl: _pdfUrl,
    isScrolling,
    isVisible,
    isRirekishoMode: _isRirekishoMode = false,
    onPageChange,
    onRefresh,
    onDownload,
  }: FunctionAreaProps): ReactElement => {
    // フォーカスモード状態を取得
    const isFocusMode = useSelector(selectFocusMode);
    const theme = useTheme();
    const prefersDarkMode = isFocusMode || theme.palette.mode === 'dark';

    /**
     * 前のページに移動するハンドラー
     *
     * @returns {void}
     */
    const handlePrevPage = useCallback((): void => {
      if (page > 1) {
        onPageChange(page - 1);
      }
    }, [page, onPageChange]);

    /**
     * 次のページに移動するハンドラー
     *
     * @returns {void}
     */
    const handleNextPage = useCallback((): void => {
      if (page < pageTotal) {
        onPageChange(page + 1);
      }
    }, [page, pageTotal, onPageChange]);

    return (
      <Box
        role="toolbar"
        aria-label="PDFプレビューコントロール"
        sx={{
          position: 'absolute',
          bottom: { xs: 16, sm: 24 },
          left: '50%', // PDFプレビューエリアの中央
          transform: isScrolling ? 'translateX(-50%) scale(0.95)' : 'translateX(-50%)', // 中央揃えのための変換
          zIndex: 9999,
          backgroundColor: getFocusModeValue(FOCUS_MODE_BACKGROUNDS, 'primary', prefersDarkMode),
          backdropFilter: 'blur(12px)',
          borderRadius: { xs: 2, sm: 3 },
          boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'primary', prefersDarkMode),
          border: `2px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'primary', prefersDarkMode)}`,
          padding: { xs: '6px 10px', sm: '10px 16px' },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 'auto', md: '280px' }, // デスクトップで最大幅制限
          display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' }, // デスクトップ（lg）以上のみ表示
          opacity: !isVisible ? 0 : isScrolling ? 0.4 : 1,
          transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)', // よりゆっくりとした自然なイージング
          // スクロール中の視覚的フィードバックを強化
          filter: isScrolling ? 'blur(0.5px)' : 'none',
          '&:hover': {
            opacity: 1,
            transform: 'translateX(-50%) translateY(-4px) scale(1.02)',
            boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'hover', prefersDarkMode),
            backgroundColor: getFocusModeValue(FOCUS_MODE_BACKGROUNDS, 'darker', prefersDarkMode),
            filter: 'none',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -1,
            left: -1,
            right: -1,
            bottom: -1,
            background: getFocusModeValue(FOCUS_MODE_GRADIENTS, 'before', prefersDarkMode),
            borderRadius: 'inherit',
            zIndex: -1,
            opacity: 0,
            transition: 'opacity 0.3s ease',
          },
          '&:hover::before': {
            opacity: 1,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.5, sm: 0.75 },
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            width: '100%',
          }}
        >
          {/* 前のページボタン */}
          <IconButton
            onClick={handlePrevPage}
            size="small"
            aria-label="前のページ"
            disabled={page <= 1 || pageTotal <= 0}
            sx={{
              backgroundColor: isScrolling
                ? getFocusModeValue(FOCUS_MODE_BUTTON_BACKGROUNDS, 'primaryLight', prefersDarkMode)
                : getFocusModeValue(FOCUS_MODE_BUTTON_BACKGROUNDS, 'primary', prefersDarkMode),
              border: `1px solid ${isScrolling ? getFocusModeValue(FOCUS_MODE_BORDERS, 'buttonLight', prefersDarkMode) : getFocusModeValue(FOCUS_MODE_BORDERS, 'button', prefersDarkMode)}`,
              color: isScrolling
                ? getFocusModeValue(FOCUS_MODE_COLORS, 'primaryLight', prefersDarkMode)
                : getFocusModeValue(FOCUS_MODE_COLORS, 'primary', prefersDarkMode),
              opacity: isScrolling ? 0.5 : 1,
              '&:hover': {
                backgroundColor: getFocusModeValue(
                  FOCUS_MODE_BUTTON_BACKGROUNDS,
                  'primaryHover',
                  prefersDarkMode
                ),
                border: `1px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'buttonHover', prefersDarkMode)}`,
                transform: 'scale(1.05)',
                boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'button', prefersDarkMode),
                opacity: 1,
              },
              '&:disabled': {
                backgroundColor: getFocusModeValue(
                  FOCUS_MODE_BUTTON_BACKGROUNDS,
                  'disabled',
                  prefersDarkMode
                ),
                border: `1px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'disabled', prefersDarkMode)}`,
                color: getFocusModeValue(FOCUS_MODE_COLORS, 'disabled', prefersDarkMode),
              },
              transition: 'all 0.2s ease',
            }}
          >
            <NavigateBefore fontSize="small" />
          </IconButton>

          {/* ページ数表示 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                textAlign: 'center',
                fontSize: { xs: '0.5rem', sm: '0.6rem' },
                opacity: isScrolling ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              aria-live="polite"
              aria-label="ページ情報"
            >
              {pageTotal > 0 ? `${page} / ${pageTotal}` : pageTotal === 0 ? 'エラー' : '読込中...'}
            </Typography>
          </Box>

          {/* 次のページボタン */}
          <IconButton
            onClick={handleNextPage}
            size="small"
            aria-label="次のページ"
            disabled={page >= pageTotal || pageTotal <= 0}
            sx={{
              backgroundColor: isScrolling
                ? getFocusModeValue(FOCUS_MODE_BUTTON_BACKGROUNDS, 'primaryLight', prefersDarkMode)
                : getFocusModeValue(FOCUS_MODE_BUTTON_BACKGROUNDS, 'primary', prefersDarkMode),
              border: `1px solid ${isScrolling ? getFocusModeValue(FOCUS_MODE_BORDERS, 'buttonLight', prefersDarkMode) : getFocusModeValue(FOCUS_MODE_BORDERS, 'button', prefersDarkMode)}`,
              color: isScrolling
                ? getFocusModeValue(FOCUS_MODE_COLORS, 'primaryLight', prefersDarkMode)
                : getFocusModeValue(FOCUS_MODE_COLORS, 'primary', prefersDarkMode),
              opacity: isScrolling ? 0.5 : 1,
              '&:hover': {
                backgroundColor: getFocusModeValue(
                  FOCUS_MODE_BUTTON_BACKGROUNDS,
                  'primaryHover',
                  prefersDarkMode
                ),
                border: `1px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'buttonHover', prefersDarkMode)}`,
                transform: 'scale(1.05)',
                boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'button', prefersDarkMode),
                opacity: 1,
              },
              '&:disabled': {
                backgroundColor: getFocusModeValue(
                  FOCUS_MODE_BUTTON_BACKGROUNDS,
                  'disabled',
                  prefersDarkMode
                ),
                border: `1px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'disabled', prefersDarkMode)}`,
                color: getFocusModeValue(FOCUS_MODE_COLORS, 'disabled', prefersDarkMode),
              },
              transition: 'all 0.2s ease',
            }}
          >
            <NavigateNext fontSize="small" />
          </IconButton>

          <IconButton
            onClick={onRefresh}
            size="small"
            aria-label="PDFを再生成"
            sx={{
              backgroundColor: isScrolling
                ? getFocusModeValue(FOCUS_MODE_BUTTON_BACKGROUNDS, 'scrollLight', prefersDarkMode)
                : getFocusModeValue(FOCUS_MODE_BUTTON_BACKGROUNDS, 'scroll', prefersDarkMode),
              border: `1px solid ${isScrolling ? getFocusModeValue(FOCUS_MODE_BORDERS, 'scrollLight', prefersDarkMode) : getFocusModeValue(FOCUS_MODE_BORDERS, 'scroll', prefersDarkMode)}`,
              color: isScrolling
                ? getFocusModeValue(FOCUS_MODE_COLORS, 'scrollLight', prefersDarkMode)
                : getFocusModeValue(FOCUS_MODE_COLORS, 'scroll', prefersDarkMode),
              opacity: isScrolling ? 0.5 : 1,
              '&:hover': {
                backgroundColor: getFocusModeValue(
                  FOCUS_MODE_BUTTON_BACKGROUNDS,
                  'scrollHover',
                  prefersDarkMode
                ),
                border: `1px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'scrollHover', prefersDarkMode)}`,
                transform: 'scale(1.05)',
                boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'scroll', prefersDarkMode),
                opacity: 1,
              },
              transition: 'all 0.2s ease',
            }}
          >
            <Refresh fontSize="small" />
          </IconButton>

          <IconButton
            onClick={onDownload}
            size="small"
            aria-label="PDFをダウンロード"
            sx={{
              backgroundColor: isScrolling
                ? getFocusModeValue(FOCUS_MODE_BUTTON_BACKGROUNDS, 'downloadLight', prefersDarkMode)
                : getFocusModeValue(FOCUS_MODE_BUTTON_BACKGROUNDS, 'download', prefersDarkMode),
              border: `1px solid ${isScrolling ? getFocusModeValue(FOCUS_MODE_BORDERS, 'downloadLight', prefersDarkMode) : getFocusModeValue(FOCUS_MODE_BORDERS, 'download', prefersDarkMode)}`,
              color: isScrolling
                ? getFocusModeValue(FOCUS_MODE_COLORS, 'downloadLight', prefersDarkMode)
                : getFocusModeValue(FOCUS_MODE_COLORS, 'download', prefersDarkMode),
              opacity: isScrolling ? 0.5 : 1,
              '&:hover': {
                backgroundColor: getFocusModeValue(
                  FOCUS_MODE_BUTTON_BACKGROUNDS,
                  'downloadHover',
                  prefersDarkMode
                ),
                border: `1px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'downloadHover', prefersDarkMode)}`,
                transform: 'scale(1.05)',
                boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'download', prefersDarkMode),
                opacity: 1,
              },
              transition: 'all 0.2s ease',
            }}
          >
            <Download fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }
);

FunctionArea.displayName = 'FunctionArea';
